import type { QueryClient } from '@tanstack/react-query'

// The one way a session ends in this tab (#834): logout button, a 401 of any
// API, a logout in another tab, or the root's session check. Pure logic with
// injected dependencies (session-end.test.ts); __root.tsx wires the app.
//
// Order: cancel running queries → empty the cache so MOUNTED observers see it
// (me = null on the live query, then every other query removed; never
// queryClient.clear(), which leaves useQuery observers on orphaned queries that
// keep rendering the old mails) → clear stores → start the purge of the user's
// offline data (pointer gone synchronously) → replace to /login → wait for the
// purge (bounded). The replace must come before the next render: page guards
// (<Navigate to="/login" />) push, which would leave /mail in the history.
//
// A network error is never a session end (offline start #785): only a real 401
// answer counts, and it is confirmed by /api/auth/me before anything is purged —
// the API also answers 401 for a wrong current password (PATCH /api/users/me).

export type SessionEndReason = 'logout' | 'unauthorized' | 'broadcast'
export type SessionProbe = 'gone' | 'offline' | 'ok'

export interface SessionEndDeps {
  queryClient: Pick<
    QueryClient,
    'cancelQueries' | 'setQueryData' | 'removeQueries' | 'getQueryData'
  >
  /** Deletes the user's offline database and forgets the offline-boot pointer. */
  purge: (userId: string) => Promise<void>
  /** Offline-boot pointer (fallback when the cache no longer knows the user). */
  lastUser: () => string | null
  /** Non-query state of the session (notification store, remembered mail scope). */
  clearStores: () => void
  /** Router navigation to /login with replace. */
  navigateToLogin: () => void | Promise<void>
  /** Current route path without the base path. */
  currentPath: () => string
  /** GET /api/auth/me: 401 = gone, fetch error = offline, anything else = ok. */
  probe: () => Promise<SessionProbe>
  /** Longest wait for the purge before /login (a blocked IndexedDB delete). */
  purgeTimeoutMs?: number
}

const ME_KEY = ['auth', 'me'] as const
const DEFAULT_PURGE_TIMEOUT_MS = 1500

/** Routes without a session: nothing to protect, no redirect needed. */
const PUBLIC_ROUTES = new Set(['/login', '/register', '/g'])
/** Routes where a 401 is no session end (auth pages, guest page #751, welcome). */
const NO_SESSION_END_ROUTES = new Set([...PUBLIC_ROUTES, '/welcome'])

export function isAuthEndpoint(apiPath: string): boolean {
  return apiPath.startsWith('/api/auth/')
}

/** True only for a 401 of a non-auth endpoint while on a session route. */
export function isSessionEndStatus(
  status: number | undefined,
  apiPath: string,
  currentPath: string,
): boolean {
  return status === 401 && !isAuthEndpoint(apiPath) && !NO_SESSION_END_ROUTES.has(currentPath)
}

function isMeKey(key: readonly unknown[]): boolean {
  return key.length === ME_KEY.length && key[0] === ME_KEY[0] && key[1] === ME_KEY[1]
}

export interface SessionEnd {
  endSession(reason: SessionEndReason, userId?: string | null): Promise<void>
  handleUnauthorized(status: number | undefined, apiPath: string): Promise<void>
  /** Marks a running logout (outbox flush + /api/auth/logout): a 401 meanwhile
   * waits for it instead of purging in parallel; the logout ends the session. */
  duringLogout<T>(logout: Promise<T>): Promise<T>
}

export function createSessionEnd(deps: SessionEndDeps): SessionEnd {
  const purgeTimeoutMs = deps.purgeTimeoutMs ?? DEFAULT_PURGE_TIMEOUT_MS
  // One session end at a time; concurrent triggers join it (exactly once).
  let ending: Promise<void> | null = null
  let confirming: Promise<void> | null = null
  let loggingOut: Promise<unknown> | null = null

  async function run(explicitUserId: string | null | undefined): Promise<void> {
    const qc = deps.queryClient
    const cached = qc.getQueryData<{ user?: { id: string } } | null>(ME_KEY)
    const userId = explicitUserId ?? cached?.user?.id ?? deps.lastUser()
    // 1. No late answer of the old session may land in the cache.
    await qc.cancelQueries().catch(() => undefined)
    // 2. Mounted observers see me = null at once (pages stop rendering the old
    //    data); the data queries go without a refetch.
    qc.setQueryData(ME_KEY, null)
    qc.removeQueries({ predicate: (query) => !isMeKey(query.queryKey) })
    try {
      deps.clearStores()
    } catch {
      // a broken store must not keep the session open
    }
    // 3. Offline data of the user; the pointer is gone before the first await.
    const purged = userId ? deps.purge(userId).catch(() => undefined) : null
    // 4. Straight to /login (history entry replaced), before the next render.
    if (!PUBLIC_ROUTES.has(deps.currentPath())) await deps.navigateToLogin()
    if (purged) {
      let timer: ReturnType<typeof setTimeout> | undefined
      await Promise.race([
        purged,
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, purgeTimeoutMs)
        }),
      ])
      clearTimeout(timer)
    }
  }

  function endSession(_reason: SessionEndReason, userId?: string | null): Promise<void> {
    if (ending) return ending
    const current = run(userId).finally(() => {
      if (ending === current) ending = null
    })
    ending = current
    return current
  }

  function handleUnauthorized(status: number | undefined, apiPath: string): Promise<void> {
    if (!isSessionEndStatus(status, apiPath, deps.currentPath())) return Promise.resolve()
    if (ending) return ending
    if (loggingOut) return loggingOut.then(noop, noop)
    if (confirming) return confirming
    const current = deps
      .probe()
      .catch((): SessionProbe => 'ok')
      .then((state) => (state === 'gone' ? endSession('unauthorized') : undefined))
      .finally(() => {
        if (confirming === current) confirming = null
      })
    confirming = current
    return current
  }

  function duringLogout<T>(logout: Promise<T>): Promise<T> {
    const current = logout.finally(() => {
      if (loggingOut === current) loggingOut = null
    })
    loggingOut = current
    return current
  }

  return { endSession, handleUnauthorized, duringLogout }
}

function noop(): void {}

// ---------------------------------------------------------------------------
// App instance (configured by the root layout)

let app: SessionEnd | null = null

export function configureSessionEnd(deps: SessionEndDeps): () => void {
  const instance = createSessionEnd(deps)
  app = instance
  return () => {
    if (app === instance) app = null
  }
}

export function endSession(reason: SessionEndReason, userId?: string | null): Promise<void> {
  return app ? app.endSession(reason, userId) : Promise.resolve()
}

export function duringLogout<T>(logout: Promise<T>): Promise<T> {
  return app ? app.duringLogout(logout) : logout
}

export function reportUnauthorized(status: number | undefined, apiPath: string): Promise<void> {
  return app ? app.handleUnauthorized(status, apiPath) : Promise.resolve()
}
